import React, { useContext, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { CacheContext, CacheContextType } from '../../context/CacheContext';
import { FollowsContext } from '../../context/FollowsContext';
import { AuthContext, MyContext } from '../../context/AuthContext';
import TimeDuration from '../../TimeDuration/index';
import { DateTime } from 'luxon';
import { stringToArray, getSchedule } from 'cron-converter';
import FeedsIcon from '../../resource/FeedsIcon';
import TrophyIcon from '../../resource/TropyIcon';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import CasinoIcon from '@mui/icons-material/Casino';
import FollowButton from '../../Follows/FollowButtonComponent';
import { useNavigate } from 'react-router-dom';
import NearlyDead from '../../components/NearlyDead';

function formatWalletShort(seconds: number): string {
  if (seconds <= 0) return '0';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d >= 365) return `${Math.floor(d / 365)}y ${d % 365}d`;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h`;
  return `${Math.floor(seconds / 60)}m`;
}

interface TrendingSidebarProps {
  /**
   * When true, render flow-layout (no fixed positioning) and skip the
   * breakpoint gate. AppShell uses this because the grid handles hiding
   * the aside on small screens. Default `false` keeps legacy behavior.
   */
  inFlow?: boolean;
}

const TrendingSidebar: React.FC<TrendingSidebarProps> = ({ inFlow = false }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { allFeeds, allPosts, allUsers } = useContext<CacheContextType>(CacheContext);
  const { follows } = useContext(FollowsContext);
  const currentUser = useContext<MyContext>(AuthContext).userRecord;
  const navigate = useNavigate();

  // Random post roulette — picks from alive non-reply posts. Excludes
  // the user's own posts so it's always a discovery action.
  const rollRandomPost = () => {
    const now = DateTime.now();
    const pool = allPosts().filter((p) =>
      !p.is_expired &&
      !p.parent_id &&
      DateTime.fromISO(p.expire_at) > now &&
      (!currentUser || p.user_record_id !== currentUser.id),
    );
    if (pool.length === 0) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    navigate(`/posts/${pick.id}`);
  };

  const wipeDate = useMemo(
    () => getSchedule(stringToArray('0 0 1 * *'), new Date(), 'America/Los_Angeles').next().toJSDate(),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const trendingFeeds = useMemo(() => {
    allPosts(); // trigger recalc when posts change
    const feeds = allFeeds();
    return feeds
      .filter((f) =>
        !f.is_expired &&
        (f.expire_at ? DateTime.fromISO(f.expire_at) > DateTime.now() : true),
      )
      .sort((a, b) => {
        // Sort by expire_at desc (most-extended = most votes/posts)
        const aExp = a.expire_at ? DateTime.fromISO(a.expire_at).toSeconds() : 0;
        const bExp = b.expire_at ? DateTime.fromISO(b.expire_at).toSeconds() : 0;
        return bExp - aExp;
      })
      .slice(0, 5);
  }, [allFeeds, allPosts]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const posts = allPosts();
    const users = allUsers();
    const now = DateTime.now();
    const activePosts = posts.filter((p) => !p.is_expired && DateTime.fromISO(p.expire_at) > now);
    const dyingCount = activePosts.filter((p) => {
      const left = DateTime.fromISO(p.expire_at).diff(now).as('hours');
      return left < 1;
    }).length;
    return { totalPosts: activePosts.length, userCount: users.length, dyingCount };
  }, [allPosts, allUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Personal stats — only for logged-in users. Derived in-memory from
  // the already-hydrated cache so this card is free (no extra queries).
  const myStats = useMemo(() => {
    if (!currentUser) return null;
    const now = DateTime.now();
    const myPosts = allPosts().filter(
      (p) => p.user_record_id === currentUser.id && !p.is_expired && DateTime.fromISO(p.expire_at) > now,
    );
    const daysSurvived = Math.max(
      0,
      Math.floor(now.diff(DateTime.fromISO(currentUser.join_date), 'days').days),
    );
    return {
      wallet: currentUser.time_wallet_seconds ?? 0,
      tokens: currentUser.permanence_tokens ?? 0,
      postsAlive: myPosts.length,
      daysSurvived,
    };
  }, [currentUser, allPosts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Top 3 wall-time leaders — derived from users cache, no extra query.
  const topThree = useMemo(() => {
    return [...allUsers()]
      .sort((a, b) => (b.time_wallet_seconds ?? 0) - (a.time_wallet_seconds ?? 0))
      .slice(0, 3);
  }, [allUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Who to follow" — users not yet followed, sorted by follower count
  const whoToFollow = useMemo(() => {
    if (!currentUser) return [];
    const followedIds = new Set(
      follows
        .filter((f) => f.user_record_id === currentUser.id && !f.is_expired && DateTime.fromISO(f.expire_at) > DateTime.now())
        .map((f) => f.parent_id),
    );
    return allUsers()
      .filter((u) => u.id !== currentUser.id && !followedIds.has(u.id))
      .slice(0, 3);
  }, [follows, allUsers, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legacy (non-inFlow) mode hides below lg. In-flow mode trusts the
  // grid media query to hide this column, so we always render when the
  // component actually mounts.
  if (!inFlow && !isDesktop) return null;

  // In-flow mode: drop the fixed positioning and breakpoint display
  // toggle. The AppShell grid gives us a sticky column with bounded
  // width — we just fill it.
  const inFlowSx = {
    width: '100%',
    maxHeight: 'calc(100dvh - var(--topbar-height) - 16px)',
    overflowY: 'auto' as const,
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'divider' },
  };
  const legacySx = {
    width: 280,
    position: 'fixed' as const,
    top: 80,
    right: 16,
    display: { xs: 'none', lg: 'block' },
    maxHeight: 'calc(100vh - 100px)',
    overflowY: 'auto' as const,
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'divider' },
  };

  return (
    <Box sx={inFlow ? inFlowSx : legacySx}>
      {/* Live platform stats */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block', animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
          Live
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`${stats.totalPosts} alive`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          {stats.dyingCount > 0 && (
            <Chip label={`${stats.dyingCount} dying`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          )}
          <Chip label={`${stats.userCount} wipers`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
        </Box>
        <Chip
          label="Random post"
          size="small"
          clickable
          onClick={rollRandomPost}
          icon={<CasinoIcon sx={{ fontSize: 14 }} />}
          sx={{
            mt: 1,
            fontSize: '0.72rem',
            height: 24,
            width: '100%',
            justifyContent: 'center',
            borderRadius: 1,
            bgcolor: 'transparent',
            border: '1px dashed',
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main', color: 'primary.main' },
          }}
        />
      </Paper>

      {/* Your Stats — compact 4-cell grid, logged-in only */}
      {currentUser && myStats && (
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.25, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <BarChartIcon sx={{ width: 16, height: 16 }} />
            Your Stats
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            {[
              // Nokia: monochrome stat tiles, only token count keeps the
              // amber accent (matches the topbar wallet ring's amber state).
              { label: 'wallet', value: formatWalletShort(myStats.wallet), color: 'text.primary' },
              { label: 'tokens', value: myStats.tokens.toString(), color: 'warning.main' },
              { label: 'alive', value: myStats.postsAlive.toString(), color: 'success.main' },
              { label: 'days', value: myStats.daysSurvived.toString(), color: 'text.primary' },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  p: 0.9,
                  borderRadius: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"VT323", monospace',
                    fontSize: '1.3rem',
                    lineHeight: 1,
                    color: s.color,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Nearly Dead — posts expiring in <1h, ranked by engagement */}
      <NearlyDead />

      {/* Top 3 leaderboard — wall-time survivors */}
      {topThree.length > 0 && (
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.25, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <EmojiEventsOutlinedIcon sx={{ width: 16, height: 16 }} />
            Top Wipers
          </Typography>
          <Box>
            {topThree.map((u, i) => {
              const medals = ['🥇', '🥈', '🥉'];
              return (
                <React.Fragment key={u.id}>
                  <Box
                    sx={{ py: 0.6, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Box sx={{ fontSize: '1.1rem', width: 22, textAlign: 'center', flexShrink: 0 }}>
                      {medals[i] ?? `#${i + 1}`}
                    </Box>
                    <Avatar
                      src={u.avatar_url ?? undefined}
                      component={Link}
                      to={`/${u.username}`}
                      aria-label={`@${u.username} profile`}
                      sx={{ width: 26, height: 26, bgcolor: 'primary.main', fontSize: '0.7rem', flexShrink: 0, textDecoration: 'none' }}
                    >
                      {u.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography
                      component={Link}
                      to={`/${u.username}`}
                      variant="body2"
                      fontWeight={700}
                      sx={{ flex: 1, minWidth: 0, textDecoration: 'none', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                      noWrap
                    >
                      {u.username}
                    </Typography>
                    <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', fontFamily: '"VT323", monospace', fontSize: '0.78rem' }}>
                      {formatWalletShort(u.time_wallet_seconds ?? 0)}
                    </Typography>
                  </Box>
                  {i < topThree.length - 1 && <Divider sx={{ my: 0.25 }} />}
                </React.Fragment>
              );
            })}
          </Box>
          <MuiLink
            component={Link}
            to="/leaderboard"
            variant="caption"
            color="text.secondary"
            underline="hover"
            sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
          >
            view full leaderboard →
          </MuiLink>
        </Paper>
      )}

      {/* Trending Feeds */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <FeedsIcon style={{ width: '16px', height: '16px' }} />
          Trending Feeds
        </Typography>

        <Box>
          {trendingFeeds.length > 0 ? (
            trendingFeeds.map((feed, index) => (
              <React.Fragment key={feed.id || index}>
                <Box
                  component={Link}
                  to={`/feeds/${feed.feed_tag}`}
                  sx={{
                    px: 0.5,
                    py: 0.75,
                    borderRadius: 1,
                    color: 'inherit',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemText
                    primary={`#${feed.feed_tag}`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                  />
                  {feed.expire_at && (
                    <Typography variant="caption" color={
                      DateTime.fromISO(feed.expire_at).diff(DateTime.now()).as('hours') < 1
                        ? 'error.main' : 'text.secondary'
                    } sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.7rem' }}>
                      <TimeDuration jsDate={DateTime.fromISO(feed.expire_at).toJSDate()} />
                    </Typography>
                  )}
                </Box>
                {index < trendingFeeds.length - 1 && <Divider sx={{ my: 0.25 }} />}
              </React.Fragment>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1 }}>
              [ NO FEEDS YET ]
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Wipe countdown */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <TrophyIcon style={{ width: '16px', height: '16px' }} />
          Next Wipe
        </Typography>
        <Box sx={{ textAlign: 'center', py: 0.5 }}>
          <Typography sx={{ fontFamily: '"VT323", monospace', fontSize: '1.75rem', lineHeight: 1, letterSpacing: 2 }}>
            <TimeDuration jsDate={wipeDate} />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
            all expired content is deleted
          </Typography>
        </Box>
      </Paper>

      {/* Who to follow */}
      {currentUser && whoToFollow.length > 0 && (
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <PersonAddIcon sx={{ width: 16, height: 16 }} />
            Who to Follow
          </Typography>
          <Box>
            {whoToFollow.map((user, i) => (
              <React.Fragment key={user.id}>
                <Box
                  sx={{ py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Avatar
                    src={user.avatar_url ?? undefined}
                    component={Link}
                    to={`/${user.username}`}
                    aria-label={`@${user.username} profile`}
                    sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem', flexShrink: 0, textDecoration: 'none' }}
                  >
                    {user.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      component={Link}
                      to={`/${user.username}`}
                      variant="body2"
                      fontWeight={700}
                      sx={{ display: 'block', textDecoration: 'none', color: 'text.primary', '&:hover': { textDecoration: 'underline' } }}
                      noWrap
                    >
                      {user.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      @{user.username}
                    </Typography>
                  </Box>
                  <FollowButton parentRecord={user} objectType="users" />
                </Box>
                {i < whoToFollow.length - 1 && <Divider sx={{ my: 0.25 }} />}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}

      {/* Privacy manifesto */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: 'success.main', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(46,125,50,0.08)' : 'rgba(46,125,50,0.04)' }}>
        <Typography variant="caption" fontWeight={700} color="success.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.75 }}>
          🔒 Privacy First
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          We don't sell your data. We don't hold onto it either. Everything expires — by design.
        </Typography>
      </Paper>

      <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} Wipehouse
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
          <MuiLink component={Link} to="/about" color="inherit" underline="hover" variant="caption">About</MuiLink>
          <MuiLink component={Link} to="/welcome" color="inherit" underline="hover" variant="caption">How it works</MuiLink>
        </Box>
      </Paper>
    </Box>
  );
};

export default TrendingSidebar;
